import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatDateTime, formatEgp, formatNumber } from '../lib/format';
import type { Campaign, Donation } from '../lib/types';
import PageHeader from '../components/PageHeader';
import MaskedPii from '../components/MaskedPii';
import { subOptionLabel } from '../lib/donationSubOptions';
import { buildHaystack, matchesAllTokens, tokenizeQuery } from '../lib/search';

// Tiny SVG sparkline. Draws a 7-point trend line inside the status
// cards — area fill + polyline + endpoint dot. Uses viewBox
// coordinates only so it scales to whatever width its container is.
// A zero-variance series renders as a flat mid-line so the card
// still shows something instead of stacking marks on top of each
// other. Height is fixed at 26 px to stay inside the card's baseline.
function Sparkline({ series, color }: { series: number[]; color: string }) {
  const w = 100;
  const h = 26;
  const pad = 2;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = Math.max(1, max - min);
  const pts = series.map((v, i) => {
    const x = pad + (i * (w - 2 * pad)) / Math.max(1, series.length - 1);
    const y = h - pad - ((v - min) / span) * (h - 2 * pad);
    return [x, y] as const;
  });
  const lastPt = pts[pts.length - 1];
  const areaPath = `M ${pts[0][0]} ${h - pad}` +
    pts.map(([x, y]) => ` L ${x} ${y}`).join('') +
    ` L ${lastPt[0]} ${h - pad} Z`;
  const linePoints = pts.map(([x, y]) => `${x},${y}`).join(' ');
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      width="100%"
      height={h}
      aria-hidden="true"
      style={{ display: 'block', marginTop: 8 }}
    >
      <path d={areaPath} fill={color} opacity="0.14" />
      <polyline
        points={linePoints}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="2.4" fill={color} />
    </svg>
  );
}

// Split "زكاة — علاج مرضى" into {group: "زكاة", sub: "علاج مرضى"}. A
// title without a dash keeps its full name as the group. Used to
// build the grouped campaign dropdown so operators can pick within a
// parent category without scrolling a flat 20-row list.
function splitCampaign(title: string): { group: string; sub: string } {
  const parts = title.split(' — ');
  if (parts.length === 1) return { group: parts[0].trim(), sub: parts[0].trim() };
  const [group, ...rest] = parts;
  return { group: group.trim(), sub: rest.join(' — ').trim() };
}

// Uses the donations_with_context view — joins campaign + urgent case
// titles server-side so the report page can show "الغرض" (purpose)
// directly without a second query per row.
type DonationRow = Donation & {
  campaign_title_ar: string | null;
  urgent_case_id: string | null;
  urgent_case_title_ar: string | null;
  urgent_case_code: string | null;
  urgent_case_featured: boolean | null;
  case_code_snapshot: string | null;
  sub_option: string | null;
};

// U-ADM-02 · pre-launch audit hardening. Server-side pagination
// through the donations_with_context view. Filters + status stay
// client-side (over the current page) — a full server-side filter
// pass is a follow-up; the immediate goal is to stop losing older
// records once the org passes 500 donations a month.
const PAGE_SIZE = 500;

export default function DonationsTransactionsPage() {
  const [rows, setRows] = useState<DonationRow[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCampaign, setFilterCampaign] = useState('');
  const [search, setSearch] = useState('');
  // Dedicated filters (operator request 2026-09-02): status + amount
  // range + date range on top of the fuzzy search box. Case code, phone
  // and donor name are already covered by the search, but status +
  // amount range + date range need real inputs — you can't type "> 500
  // in September" into a search box.
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterAmountMin, setFilterAmountMin] = useState<string>('');
  const [filterAmountMax, setFilterAmountMax] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  // Advanced-filters row is collapsed by default so the toolbar looks
  // like the old one at first glance (campaign dropdown + big search
  // box). Operator taps "فلتره متقدمه" to reveal status + amount +
  // date filters when they need them.
  const [showAdvanced, setShowAdvanced] = useState(true);
  // Detail modal — clicking "تفاصيل" on a row opens all fields
  // (donor + Paymob) in one panel so ops don't have to squint at the
  // table for the full picture.
  const [detail, setDetail] = useState<DonationRow | null>(null);

  useEffect(() => {
    void load();
    // Realtime: on any change to donations, refetch the current page.
    const channel = supabase
      .channel('donations-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'donations' },
        () => { void load(); },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const load = async () => {
    setLoading(true);
    setError(null);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const query = supabase
      .from('donations_with_context')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);
    // No server-side status filter anymore — ops wants كل عمليات
    // التبرع في قائمة واحدة، الفلترة تكون بالحملة أو الحالة فقط.
    // The Paymob-status badge on each row still makes state obvious.
    const [d, c] = await Promise.all([
      query,
      // Only refetch the campaigns list once (on the first load) — it's
      // small and doesn't churn during a paging session. The urgent-
      // cases list was dropped along with its dedicated filter dropdown;
      // case codes now live inside the unified search box.
      page === 0
        ? supabase.from('campaigns').select('*')
        : Promise.resolve({ data: campaigns, error: null } as { data: Campaign[]; error: null }),
    ]);
    if (d.error || c.error) {
      setError((d.error ?? c.error)?.message ?? 'حصل خطأ');
    } else {
      setRows((d.data ?? []) as DonationRow[]);
      if (page === 0) setCampaigns((c.data ?? []) as Campaign[]);
      setTotalCount(d.count ?? d.data?.length ?? 0);
    }
    setLoading(false);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Purpose column shows the top-level category (زكاة مال / صدقة / …)
  // AND the specific sub-option the donor picked (علاج مرضى / صدقة جارية
  // / …), joined by an em dash. Legacy rows without a sub_option fall
  // back to just the campaign title.
  //
  // Case donations render as "حالة عاجلة رقم <code>" only when the case
  // is actually flagged عاجلة (urgent_cases.featured = true — the same
  // flag the mobile app reads to draw the "عاجلة" ribbon on the cases
  // rail). Non-featured cases render as "حالة رقم <code>" — no misleading
  // عاجلة prefix, and no long description dumped into the column.
  const purposeLabel = (r: DonationRow) => {
    if (r.urgent_case_id) {
      const code = r.case_code_snapshot ?? r.urgent_case_code ?? '';
      const prefix = r.urgent_case_featured ? 'حالة عاجلة' : 'حالة';
      return code ? `${prefix} رقم ${code}` : prefix;
    }
    const base = r.campaign_title_ar ?? r.campaign_id;
    const sub = subOptionLabel(r.sub_option);
    return sub ? `${base} — ${sub}` : base;
  };

  // Case code (snapshot at donation time) so archived / renamed cases
  // still surface the identifier the donor keyed in. Falls back to the
  // urgent case's current code if the snapshot is missing.
  const caseCodeOf = (r: DonationRow): string | null =>
    r.case_code_snapshot ?? r.urgent_case_code ?? null;

  // Unified fuzzy search (see admin/src/lib/search.ts) across every
  // visible field: donor identity, amount, order id, case code, and the
  // rendered purpose label ("زكاة مال — كفالات"). Words can appear in
  // any order and Arabic normalization is handled by the shared helper.
  const searchTokens = tokenizeQuery(search);

  // Payment status here mirrors the badge on each row. "abandoned" is
  // a virtual sub-state of failed (paymob_transaction_id starts with
  // "abandoned-"), so we can't just check r.status.
  const isAbandoned = (r: DonationRow) =>
    r.status === 'failed' && !!r.paymob_transaction_id?.startsWith('abandoned-');

  const amountMin = Number(filterAmountMin);
  const amountMax = Number(filterAmountMax);
  const dateFromMs = filterDateFrom ? new Date(filterDateFrom).getTime() : NaN;
  // Inclusive end: bump to end-of-day so a date-only "to" catches
  // everything on that day.
  const dateToMs = filterDateTo
    ? new Date(filterDateTo).getTime() + 24 * 60 * 60 * 1000 - 1
    : NaN;

  const filtered = useMemo(() => {
    // Donation rows store the parent campaign id (`zakat`) plus a
    // `sub_option` short slug (`zakat-dialysis`). The dropdown values
    // for child campaigns are the full "parent:child" id
    // (`zakat:zakat-dialysis`). When the operator picks a child, match
    // rows whose (campaign_id, sub_option) pair equals that split.
    // When they pick the parent (`zakat`) it stays a straight
    // campaign_id match — that catches every row under the bucket.
    const filterParent = filterCampaign.includes(':') ? filterCampaign.split(':', 2)[0] : filterCampaign;
    const filterSubTail = filterCampaign.includes(':') ? filterCampaign.split(':', 2)[1] : '';
    return rows.filter((r) => {
      if (filterCampaign) {
        if (r.campaign_id !== filterParent) return false;
        if (filterSubTail && r.sub_option !== filterSubTail) return false;
      }
      if (filterStatus) {
        if (filterStatus === 'abandoned') {
          if (!isAbandoned(r)) return false;
        } else if (filterStatus === 'failed') {
          if (r.status !== 'failed' || isAbandoned(r)) return false;
        } else if (r.status !== filterStatus) return false;
      }
      if (filterAmountMin && !Number.isNaN(amountMin) && r.amount_egp < amountMin) return false;
      if (filterAmountMax && !Number.isNaN(amountMax) && r.amount_egp > amountMax) return false;
      if (filterDateFrom && !Number.isNaN(dateFromMs)) {
        if (new Date(r.created_at).getTime() < dateFromMs) return false;
      }
      if (filterDateTo && !Number.isNaN(dateToMs)) {
        if (new Date(r.created_at).getTime() > dateToMs) return false;
      }
      if (searchTokens.length === 0) return true;
      const haystack = buildHaystack([
        r.donor_name, r.donor_email, r.donor_phone,
        r.merchant_order_id, purposeLabel(r), r.amount_egp,
        caseCodeOf(r), r.paymob_transaction_id,
      ]);
      return matchesAllTokens(haystack, searchTokens);
    });
    // purposeLabel + caseCodeOf are pure and stable; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filterCampaign, filterStatus, filterAmountMin, filterAmountMax, filterDateFrom, filterDateTo, search]);

  const exportCsv = () => {
    const header = [
      'donor_name',
      'donor_email',
      'donor_phone',
      'amount_egp',
      'purpose',
      'status',
      'merchant_order_id',
      'paymob_transaction_id',
      'created_at',
    ];
    const csvRows = [
      header,
      ...filtered.map((r) => [
        r.donor_name,
        r.donor_email,
        r.donor_phone,
        String(r.amount_egp),
        purposeLabel(r),
        r.status,
        r.merchant_order_id,
        r.paymob_transaction_id ?? '',
        r.created_at,
      ]),
    ];
    const csv = csvRows
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell ?? '');
            return /[",\n]/.test(cellStr) ? `"${cellStr.replace(/"/g, '""')}"` : cellStr;
          })
          .join(','),
      )
      .join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Status counts over the filtered page — powers the four cards
  // that sit at the top of the log so operators see the mix at a
  // glance before scrolling into individual rows.
  const paidRows = filtered.filter((r) => r.status === 'paid');
  const pendingRows = filtered.filter((r) => r.status === 'pending');
  const failedRows = filtered.filter((r) => r.status === 'failed' && !isAbandoned(r));
  const refundedRows = filtered.filter((r) => r.status === 'refunded');
  const abandonedRows = filtered.filter((r) => isAbandoned(r));
  const paidCount = paidRows.length;
  const pendingCount = pendingRows.length;
  const failedCount = failedRows.length;
  const refundedCount = refundedRows.length;
  const noPaymobCount = abandonedRows.length;
  const sumEgp = (list: DonationRow[]) => list.reduce((acc, r) => acc + (Number(r.amount_egp) || 0), 0);
  // Per-status amounts so each card can show both the count and the
  // EGP value under it (operator directive 2026-09-09).
  const paidAmount = sumEgp(paidRows);
  const pendingAmount = sumEgp(pendingRows);
  const failedAmount = sumEgp(failedRows);
  const noPaymobAmount = sumEgp(abandonedRows);
  // Paymob-side top-line KPIs, mirroring the Paymob dashboard's own
  // "Total Sales / Total transactions / Refunded transactions" tiles.
  // Total sales here matches Paymob's own definition: sum of every
  // transaction they've seen, regardless of outcome (paid + pending +
  // failed). That way the number lines up 1-to-1 with what the
  // operator reads on Paymob's dashboard for the same window. The
  // "money actually landed" figure is still available on the مكتملة
  // status card underneath.
  const paymobTotalSales = paidAmount + pendingAmount + failedAmount;
  const paymobTxCount = paidCount + pendingCount + failedCount + refundedCount;

  // Sparkline series — count per day for the last 7 days, bucket 0 =
  // 6 days ago, bucket 6 = today. Powers the mini trend line at the
  // bottom of each status card (operator directive 2026-09-09).
  const buildSpark = (list: DonationRow[]): number[] => {
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const day = 24 * 60 * 60 * 1000;
    const buckets = new Array(7).fill(0);
    for (const r of list) {
      const t = new Date(r.created_at).getTime();
      const rowMidnight = new Date(new Date(t).setHours(0, 0, 0, 0)).getTime();
      const daysAgo = Math.round((todayMidnight - rowMidnight) / day);
      if (daysAgo >= 0 && daysAgo < 7) buckets[6 - daysAgo]++;
    }
    return buckets;
  };
  const paidSpark = buildSpark(paidRows);
  const pendingSpark = buildSpark(pendingRows);
  const failedSpark = buildSpark(failedRows);
  const noPaymobSpark = buildSpark(abandonedRows);

  // Group campaigns by ID hierarchy — parent rows have no colon in
  // their id (`zakat`, `sadaqa`, `gaza`, `c4835befefa`), children
  // use `parent:child` (`zakat:zakat-ibn-sabeel`). Mirrors the
  // "أوجه التبرع" management page so the dropdown reads like the
  // admin's own catalogue: each parent title with its children
  // grouped beneath it. Falls back to sorting children alphabetically
  // inside each group.
  const campaignGroups = useMemo(() => {
    const parentsById = new Map<string, Campaign>();
    const childrenByParent = new Map<string, Campaign[]>();
    for (const c of campaigns) {
      const parentId = c.id.split(':')[0];
      if (c.id.includes(':')) {
        const list = childrenByParent.get(parentId) ?? [];
        list.push(c);
        childrenByParent.set(parentId, list);
      } else {
        parentsById.set(parentId, c);
      }
    }
    const groups: Array<{ parent: Campaign; children: Campaign[] }> = [];
    for (const [id, parent] of parentsById) {
      const children = (childrenByParent.get(id) ?? [])
        .slice()
        .sort((a, b) => a.title_ar.localeCompare(b.title_ar, 'ar'));
      groups.push({ parent, children });
    }
    // Orphaned children (parent row missing) fall back to their own
    // synthetic group so nothing disappears from the dropdown.
    for (const [id, list] of childrenByParent) {
      if (!parentsById.has(id)) {
        groups.push({
          parent: { id, title_ar: id, active: true } as Campaign,
          children: list.slice().sort((a, b) => a.title_ar.localeCompare(b.title_ar, 'ar')),
        });
      }
    }
    return groups.sort((a, b) => a.parent.title_ar.localeCompare(b.parent.title_ar, 'ar'));
  }, [campaigns]);

  // Row-level badge: paymob_transaction_id carries a semantic marker for
  // rows the reconciler resolved without a live Paymob transaction —
  // 'abandoned-no-paymob-order' means the donor never made it through
  // to Paymob (checkout closed before the redirect landed). We surface
  // that as its own label so ops doesn't confuse it with a real refusal.
  const badgeFor = (row: Pick<Donation, 'status' | 'paymob_transaction_id'>) => {
    const { status, paymob_transaction_id: txId } = row;
    if (status === 'paid') return <span className="badge badge-success">مكتمل</span>;
    if (status === 'pending') return <span className="badge badge-pending">قيد المعالجة</span>;
    if (status === 'refunded') return <span className="badge badge-muted">مسترد</span>;
    if (status === 'expired') return <span className="badge badge-muted">منتهي</span>;
    if (typeof txId === 'string' && txId.startsWith('abandoned-')) {
      return <span className="badge badge-muted" title="لم يكمل المتبرع خطوة الدفع — العملية لم تصل لصفحة Paymob" style={{ whiteSpace: 'nowrap' }}>لم يصل لـ Paymob</span>;
    }
    return <span className="badge badge-failed">فشل</span>;
  };

  return (
    <>
      <PageHeader
        eyebrow={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            التبرعات
            {/* LIVE badge — Mersal yellow with a soft pulsing dot so
                the operator's eye picks up "live view" immediately.
                Animation is a subtle 1.4 s breath, respectful of
                prefers-reduced-motion via the @media rule below. */}
            <style>{`
              @keyframes mersal-live-pulse {
                0%, 100% { transform: scale(1); box-shadow: 0 0 0 3px rgba(254, 200, 48, 0.30); }
                50%      { transform: scale(1.25); box-shadow: 0 0 0 6px rgba(254, 200, 48, 0.05); }
              }
              @keyframes mersal-live-glow {
                0%, 100% { box-shadow: 0 0 0 rgba(254, 200, 48, 0); }
                50%      { box-shadow: 0 0 10px rgba(254, 200, 48, 0.55); }
              }
              .mersal-live-badge      { animation: mersal-live-glow 1.4s ease-in-out infinite; }
              .mersal-live-badge-dot  { animation: mersal-live-pulse 1.4s ease-in-out infinite; }
              @media (prefers-reduced-motion: reduce) {
                .mersal-live-badge, .mersal-live-badge-dot { animation: none; }
              }
            `}</style>
            <span
              className="mersal-live-badge"
              title="التحديث لحظي — أي عملية Paymob بتظهر هنا فوراً"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: 'rgba(254, 200, 48, 0.14)',
                color: '#8a6100',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                border: '1px solid rgba(254, 200, 48, 0.55)',
              }}
            >
              <span
                className="mersal-live-badge-dot"
                style={{
                  width: 6, height: 6, borderRadius: 999,
                  background: '#fec830',
                }}
              />
              LIVE
            </span>
          </span>
        }
        title="سجل التبرعات"
        subtitle={
          totalCount > rows.length
            ? `صفحة ${(page + 1).toLocaleString('ar-EG')} من ${totalPages.toLocaleString('ar-EG')} — ${rows.length.toLocaleString('ar-EG')} من إجمالي ${totalCount.toLocaleString('ar-EG')} تبرع — التحديث تلقائي مع كل عملية Paymob جديدة`
            : `كل عمليات التبرع من التطبيق (${rows.length.toLocaleString('ar-EG')}) — التحديث تلقائي مع كل عملية Paymob جديدة`
        }
        actions={
          <button className="btn btn-secondary" onClick={exportCsv} disabled={filtered.length === 0}>
            تحميل CSV ({filtered.length})
          </button>
        }
      />

      {error && <div className="error-inline">{error}</div>}

      {/* Status cards — live count of the currently-filtered rows so
          the operator sees the mix at a glance before scrolling.
          The first three (paid / pending / failed) all rode through
          Paymob and get grouped under a "معاملات Paymob" header with
          the total right beside it. The fourth card (لم يصل لـ Paymob)
          sits on its own since those donors never actually reached
          Paymob's checkout. */}
      <style>{`
        .status-card {
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 16px;
          background: #fff;
          border-inline-start-width: 4px;
          border-inline-start-style: solid;
          transition: transform 160ms ease, box-shadow 160ms ease;
        }
        .status-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }
        .paymob-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin: 0 auto 20px;
          max-width: 1100px;
          align-items: stretch;
        }
        .paymob-section {
          flex: 1 1 640px;
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 14px 16px 18px;
          background: #fbfbfd;
          position: relative;
          overflow: hidden;
        }
        /* Sweeping accent bar along the top edge on hover — a single
           gentle pass in Paymob blue that reads as "attention on this
           block" without moving the frame itself. LTR sweep: starts
           left-of-frame, ends right-of-frame; the visual direction
           feels the same in an RTL page because the gradient itself
           is symmetric. */
        .paymob-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent 0%, rgba(26, 75, 255, 0) 15%, rgba(26, 75, 255, 0.85) 50%, rgba(26, 75, 255, 0) 85%, transparent 100%);
          transform: translateX(-100%);
          transition: transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1);
          pointer-events: none;
        }
        .paymob-section:hover::before {
          transform: translateX(100%);
        }
        .no-paymob-card {
          flex: 0 1 220px;
          min-width: 200px;
          align-self: center;
        }
        @media (prefers-reduced-motion: reduce) {
          .paymob-section, .paymob-section:hover {
            transform: none;
            transition: none;
          }
        }
        .paymob-section-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px dashed var(--border);
        }
        .paymob-section-title {
          font-size: 14px;
          font-weight: 700;
          color: var(--text-primary);
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .paymob-section-total {
          font-size: 13px;
          color: var(--text-secondary);
        }
        .paymob-section-total b {
          color: var(--text-primary);
          font-weight: 800;
          font-size: 18px;
          margin-inline-start: 4px;
        }
      `}</style>

      <div className="paymob-row">
      <div className="paymob-section">
        <div className="paymob-section-header">
          <span className="paymob-section-title">
            <span style={{ color: 'var(--text-primary)' }}>معاملات</span>
            {/* Paymob-brand chip so the operator recognises the section
                at a glance — filled pill in Paymob's brand blue with
                the vendor name only, no attempt to reproduce the actual
                logo mark. */}
            <span
              style={{
                background: '#1a4bff',
                color: '#fff',
                padding: '3px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.3,
                display: 'inline-block',
              }}
            >
              Paymob
            </span>
          </span>
        </div>

        {/* Paymob-style top-line KPIs: total sales, total transactions,
            refunded — mirroring what the operator sees on Paymob's own
            dashboard so the two numbers can be sanity-checked side by
            side without switching tabs. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginBottom: 14,
          }}
        >
          {[
            { name: 'إجمالي المبيعات', value: formatEgp(paymobTotalSales), color: '#29b587' },
            { name: 'عدد الحركات', value: formatNumber(paymobTxCount), color: '#4f75ff' },
            { name: 'حركات مستردة', value: formatNumber(refundedCount), color: '#8a94a0' },
          ].map((s) => (
            <div
              key={s.name}
              className="status-card"
              style={{ borderInlineStartColor: s.color }}
            >
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.name}</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6, color: 'var(--text-primary)' }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Paymob-side status breakdown — مكتملة / قيد المعالجة / فشلت.
            Sits inside the same frame as the KPIs so hovering the
            frame lights up the whole Paymob block. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          {[
            { name: 'مكتملة', count: paidCount, amount: paidAmount, color: '#29b587', spark: paidSpark },
            { name: 'قيد المعالجة', count: pendingCount, amount: pendingAmount, color: '#f9b921', spark: pendingSpark },
            { name: 'فشلت', count: failedCount, amount: failedAmount, color: '#c8863a', spark: failedSpark },
          ].map((s) => (
            <div
              key={s.name}
              className="status-card"
              style={{ borderInlineStartColor: s.color, textAlign: 'center' }}
            >
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.name}</div>
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6, color: s.color }}>
                {formatNumber(s.count)}
              </div>
              <div style={{ fontSize: 12, marginTop: 4, color: 'var(--text-secondary)', fontWeight: 600 }}>
                {formatEgp(s.amount)}
              </div>
              <Sparkline series={s.spark} color={s.color} />
            </div>
          ))}
        </div>
      </div>

        {/* Never-reached-Paymob card — sits BESIDE the Paymob frame
            on the same row (not inside it), because those donors
            never entered Paymob's flow. Grey stripe + no frame
            wrapper mark it as a different bucket. */}
        <div
          className="status-card no-paymob-card"
          style={{ borderInlineStartColor: '#8a94a0', textAlign: 'center' }}
        >
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>لم يصل لـ Paymob</div>
          <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6, color: '#8a94a0' }}>
            {formatNumber(noPaymobCount)}
          </div>
          <div style={{ fontSize: 12, marginTop: 4, color: 'var(--text-secondary)', fontWeight: 600 }}>
            {formatEgp(noPaymobAmount)}
          </div>
          <Sparkline series={noPaymobSpark} color="#8a94a0" />
        </div>
      </div>

      {/* Toolbar: campaign filter (grouped by parent) + fuzzy search
          + clear-all when any filter is active. Advanced-filters row
          below is always open (operator directive 2026-09-09). */}
      <div className="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select value={filterCampaign} onChange={(e) => setFilterCampaign(e.target.value)}>
          <option value="">كل الحملات</option>
          {campaignGroups.map(({ parent, children }) => (
            children.length > 0 ? (
              // The optgroup label IS the specialty header (زكاة مال,
              // صدقة, قوافل دعم غزة…) — only the actual purposes sit
              // as selectable options underneath. Parent-bucket picks
              // stay reachable via the sub-options, so nothing hides.
              <optgroup key={parent.id} label={parent.title_ar}>
                {children.map((c) => {
                  const { sub } = splitCampaign(c.title_ar);
                  return (
                    <option key={c.id} value={c.id}>
                      {sub}
                    </option>
                  );
                })}
              </optgroup>
            ) : (
              <option key={parent.id} value={parent.id}>
                {parent.title_ar}
              </option>
            )
          ))}
        </select>
        <input
          type="search"
          placeholder="بحث عن أي حاجة: اسم، إيميل، موبايل، مبلغ، غرض، كود الحالة…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
        />
        {(filterStatus || filterAmountMin || filterAmountMax || filterDateFrom || filterDateTo || filterCampaign || search) && (
          <button
            type="button"
            className="btn btn-small btn-secondary"
            onClick={() => {
              setFilterCampaign(''); setFilterStatus('');
              setFilterAmountMin(''); setFilterAmountMax('');
              setFilterDateFrom(''); setFilterDateTo(''); setSearch('');
            }}
          >
            مسح الفلترة
          </button>
        )}
      </div>

      {showAdvanced && (
        <div className="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">كل حالات الدفع</option>
            <option value="paid">مكتمل</option>
            <option value="failed">فشل</option>
            <option value="abandoned">لم يصل باي موب</option>
            <option value="pending">قيد المراجعة</option>
            <option value="refunded">مسترد</option>
          </select>
          <input
            type="number"
            placeholder="المبلغ من (ج.م)"
            value={filterAmountMin}
            onChange={(e) => setFilterAmountMin(e.target.value)}
            min="0"
          />
          <input
            type="number"
            placeholder="المبلغ لـ (ج.م)"
            value={filterAmountMax}
            onChange={(e) => setFilterAmountMax(e.target.value)}
            min="0"
          />
          {/* Inline labels so the operator sees which date is "من"
              (start) and which is "إلى" (end) without having to hover
              for the tooltip. Label + input grouped in a flex span so
              they stay together when the toolbar wraps. */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>
            <span>من تاريخ</span>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              title="من تاريخ"
            />
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>
            <span>إلى تاريخ</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              title="إلى تاريخ"
            />
          </span>
          {/* Reset-dates button — sits right next to the two date
              pickers so operators can zero them out in one click
              without touching the other filters or the big "مسح كل
              الفلاتر" up top. Disabled when both dates are empty. */}
          <button
            type="button"
            onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }}
            disabled={!filterDateFrom && !filterDateTo}
            title="مسح التواريخ"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 700,
              color: (!filterDateFrom && !filterDateTo) ? 'var(--text-secondary)' : 'var(--text-primary)',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 8,
              cursor: (!filterDateFrom && !filterDateTo) ? 'default' : 'pointer',
              opacity: (!filterDateFrom && !filterDateTo) ? 0.55 : 1,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8"/>
              <path d="M21 3v5h-5"/>
              <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16"/>
              <path d="M3 21v-5h5"/>
            </svg>
            مسح التواريخ
          </button>
        </div>
      )}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="card empty">مفيش عمليات مطابقة.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>المتبرع</th>
              <th>الإيميل</th>
              <th>الموبايل</th>
              <th>المبلغ</th>
              <th>كود الحالة</th>
              <th>الغرض (لأي حاجة اتبرع)</th>
              <th>حالة الدفع (Paymob)</th>
              <th>وقت التبرع</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td>{r.donor_name}</td>
                <td>
                  <MaskedPii value={r.donor_email} kind="email" />
                </td>
                <td>
                  <MaskedPii value={r.donor_phone} kind="tel" style={{ direction: 'ltr' }} />
                </td>
                <td>
                  <strong>{formatEgp(r.amount_egp)}</strong>
                </td>
                <td>
                  {caseCodeOf(r) ? (
                    <span className="badge" style={{ background: '#fff3d6', color: '#8a5a00', fontFamily: 'monospace', letterSpacing: 0.5 }}>
                      {caseCodeOf(r)}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>—</span>
                  )}
                </td>
                <td>{purposeLabel(r)}</td>
                <td>{badgeFor(r)}</td>
                <td>{formatDateTime(r.created_at)}</td>
                <td>
                  <button className="btn btn-small btn-secondary" onClick={() => setDetail(r)}>
                    تفاصيل
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/*
        U-ADM-02 · pre-launch audit hardening. Pagination for donations
        so the finance team never loses older rows once the org passes
        500 per month. Filters + search are still client-side over the
        current page — a full server-side filter pass is a follow-up.
      */}
      {detail && (
        <div className="modal-backdrop" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تفاصيل التبرع</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setDetail(null)}>
                إغلاق
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 4 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>المتبرع</div>
                <div><strong>{detail.donor_name}</strong></div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>المبلغ</div>
                <div><strong>{formatEgp(detail.amount_egp)}</strong></div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>الإيميل</div>
                <MaskedPii value={detail.donor_email} kind="email" />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>الموبايل</div>
                <MaskedPii value={detail.donor_phone} kind="tel" style={{ direction: 'ltr' }} />
              </div>
              {detail.donor_address && (
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>العنوان</div>
                  <div>{detail.donor_address}</div>
                </div>
              )}
              <div style={{ gridColumn: 'span 2' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>الغرض</div>
                <div>{purposeLabel(detail)}</div>
              </div>
              {caseCodeOf(detail) && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>كود الحالة</div>
                  <div style={{ fontFamily: 'monospace' }}>{caseCodeOf(detail)}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>حالة الدفع</div>
                {badgeFor(detail)}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>رقم الطلب (Merchant Order ID)</div>
                <div style={{ fontFamily: 'monospace', direction: 'ltr', fontSize: 12 }}>{detail.merchant_order_id}</div>
              </div>
              {detail.paymob_transaction_id && (
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>رقم عملية Paymob</div>
                  <div style={{ fontFamily: 'monospace', direction: 'ltr', fontSize: 12 }}>{detail.paymob_transaction_id}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>وقت البدء</div>
                <div style={{ fontSize: 13 }}>{formatDateTime(detail.created_at)}</div>
              </div>
              {detail.updated_at && detail.updated_at !== detail.created_at && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 2 }}>آخر تحديث (Paymob webhook)</div>
                  <div style={{ fontSize: 13 }}>{formatDateTime(detail.updated_at)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {totalCount > PAGE_SIZE && !loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 16,
            padding: 12,
            border: '1px solid var(--border)',
            borderRadius: 10,
            background: 'var(--panel)',
          }}
        >
          <button
            className="btn btn-secondary btn-small"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            ← أحدث
          </button>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            صفحة {(page + 1).toLocaleString('ar-EG')} من {totalPages.toLocaleString('ar-EG')}
            <span style={{ marginInlineStart: 8, opacity: 0.7 }}>
              (٥٠٠ تبرع لكل صفحة)
            </span>
          </div>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
          >
            أقدم →
          </button>
        </div>
      )}
    </>
  );
}
